import { Link } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import { formatMoney, formatPayoutDay, nextPayoutDate } from '~/entities/ticket/lib/ticket-earnings'
import { useTicketEarnings } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { Card } from '~/shared/ui/card'

export function TicketEarningsCard({ locale, now }: { locale: string; now: number }) {
  const { businessId, scope, isLoading } = useProfileScope()
  const hostProfileId = scope.kind === 'organization' ? undefined : scope.profileId
  const { data } = useTicketEarnings(businessId ?? '', hostProfileId, !isLoading)

  if (!data?.events.some((row) => row.sellsPaid)) return null

  const money = (cents: number) => formatMoney(cents, data.currency, locale)
  const next = nextPayoutDate(data.events, new Date(now))

  return (
    <Link
      to="/dashboard/earnings"
      className="group rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <Card className="@container flex flex-col gap-4 p-6 transition-colors duration-150 ease-spring group-hover:bg-accent/40 sm:p-7">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="font-semibold font-serif text-foreground text-xl tracking-tight">
            {m.overviewTicketEarnings()}
          </h2>
          <span className="flex shrink-0 items-center gap-1 font-medium text-muted-foreground text-xs transition-colors group-hover:text-foreground">
            {m.overviewTicketAll()}
            <ArrowUpRight aria-hidden className="size-3.5" />
          </span>
        </div>
        <div className="flex flex-col gap-4 @xl:flex-row @xl:items-end @xl:justify-between @xl:gap-8">
          <div className="flex flex-col gap-1.5">
            <p className="font-semibold font-serif text-3xl text-foreground tabular-nums leading-none tracking-tight">
              {money(data.totals.netCents)}
            </p>
            <p className="text-muted-foreground text-sm">{m.overviewTicketEarningsHint()}</p>
          </div>
          {data.totals.outstandingCents > 0 ? (
            <p className="flex flex-wrap gap-x-3 gap-y-1 border-border/70 border-t pt-3 text-sm @xl:flex-col @xl:items-end @xl:border-t-0 @xl:pt-0 @xl:text-right">
              <span className="text-foreground tabular-nums">
                {m.overviewTicketOutstanding({ amount: money(data.totals.outstandingCents) })}
              </span>
              {next ? (
                <span className="text-muted-foreground">
                  {m.earningsNextPayout({ date: formatPayoutDay(next, locale) })}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </Card>
    </Link>
  )
}
